import React, { useState, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const ServicesScene = lazy(() => import('./ServicesScene'));

const SERVICES = [
  {
    n: '01',
    title: 'Custom SaaS Solutions',
    desc: 'Purpose-built software that replaces spreadsheets and busywork — shipped fast, scaled sensibly.',
  },
  {
    n: '02',
    title: 'Interactive 3D Websites',
    desc: 'Award-caliber, WebGL-driven experiences that make your brand impossible to scroll past.',
  },
  {
    n: '03',
    title: 'Mobile App Solutions',
    desc: 'Native-feeling iOS & Android apps your customers actually keep on their home screen.',
  },
  {
    n: '04',
    title: 'Digital Marketing',
    desc: 'Full-funnel creative and campaigns engineered around one metric: measurable growth.',
  },
];

export default function Services({ reduced }) {
  const [active, setActive] = useState(0);

  return (
    <section id="services" className="relative py-24 md:py-40 bg-bone" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-16 md:mb-24">
          <h2 className="serif tracking-tighter text-[11vw] md:text-[6vw] leading-none">
            What <span className="italic">we</span> do
          </h2>
          <span className="hidden md:block font-sans text-xs uppercase tracking-[0.3em] text-graphite">
            Four disciplines
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            {SERVICES.map((s, i) => (
              <motion.button
                key={s.n}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`w-full text-left border-t border-black/10 py-8 group transition-colors duration-500 ${
                  active === i ? 'text-obsidian' : 'text-obsidian/40'
                }`}
                data-testid={`service-${i}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-baseline gap-5">
                    <span className="font-sans text-xs tracking-[0.2em] pt-2">{s.n}</span>
                    <span className="serif text-4xl md:text-5xl tracking-tight">{s.title}</span>
                  </div>
                  <ArrowUpRight
                    className={`mt-2 shrink-0 transition-transform duration-500 ${
                      active === i ? 'rotate-0 text-signal' : 'rotate-45'
                    }`}
                    size={28}
                  />
                </div>
                <motion.p
                  animate={{ height: active === i ? 'auto' : 0, opacity: active === i ? 1 : 0 }}
                  className="overflow-hidden font-sans text-base text-graphite max-w-md pl-10 mt-0"
                >
                  <span className="block pt-4">{s.desc}</span>
                </motion.p>
              </motion.button>
            ))}
            <div className="border-t border-black/10" />
          </div>

          <div className="relative h-[420px] md:h-[560px] md:sticky md:top-28">
            {reduced ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-48 h-48 rounded-full bg-obsidian" />
              </div>
            ) : (
              <Suspense fallback={<div className="w-full h-full" />}>
                <ServicesScene index={active} />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
